import React from "react";
import Loader from "shared-anpost-components/components/loader/Loader";
import styled from "styled-components";
import { format } from "date-fns";
import Pagination from "shared-anpost-components/components/form/pagination/Pagination";
import { Link } from "react-router-dom";
import { CAMPAIGN_PRODUCT_IDS, STATUS, STATUS_STYLE_MAP, formatAmount } from "../utils";
import { Box, Button, Text, Tag, mq } from "../../ui";
import { downloadDmOrderInvoice } from "../../../../../services/Api";

/**
 * @param {Object} props
 * @param {{Id: string, OrderDate: string, OrderNumber: string, Status: {Name: string}, TotalPriceIncVat: number}[]} props.items
 * @param {unknown[] | undefined} props.items
 * @param {number} props.totalPages
 * @param {number} props.pageSize
 * @param {number} props.currentPage
 * @param {Function} props.onPageChange
 * @param {boolean} props.isLoading
 * @param {string | undefined} props.error
 */
export function DmOrderList({ items, isLoading, error, totalPages, totalRecords, pageSize, onPageChange, pageNumber }) {
    if (error) return null;
    if (items?.length === 0) return <NoTransactionHistory />;
    return (
        <Box gap="16px">
            {isLoading ? <Loader local /> : items?.map(trx => <DmTransactionCard key={trx.Id} {...trx} />)}
            {totalPages > 1 && <Pagination pageCount={totalRecords} pageSize={pageSize} pageChange={onPageChange} currentPage={pageNumber} isLoading={isLoading} />}
        </Box>
    );
}

/**
 * @param {{canNavigate:boolean, showOverview:boolean, Id: string, OrderDate: string, OrderNumber: string, Status: {Name: string}, TotalPriceIncVat?: number, TotalPriceExVat?:number, OrderItems:{Id:string,Quantity:number,CustomerDataItem:{Product: {Id: string, Name: string}}}[]}} props
 */
export function DmTransactionCard(props) {
    const { OrderDate, OrderNumber, OrderItems, TotalPriceIncVat, TotalPriceExVat, Status, canNavigate = true, showOverview = true, ...rest } = props;
    const orderDate = format(new Date(OrderDate), "dd MMM yyyy");

    const tagStyle = STATUS_STYLE_MAP[Status.Name] || {
        dotColor: "#217BBE",
        backgroundColor: "#217BBE26"
    };

    const price = (() => {
        if (typeof TotalPriceIncVat === "number") return formatAmount(TotalPriceIncVat);
        if (typeof TotalPriceExVat === "number") return `${formatAmount(TotalPriceExVat)} ex VAT`;
    })();

    // count similar products
    const productList = React.useMemo(() => {
        const productCount = OrderItems.reduce((acc, item) => {
            const isCampaignItem = CAMPAIGN_PRODUCT_IDS.includes(item?.CustomerDataItem?.Product?.Id);
            if (!isCampaignItem) return acc;
            const { CustomerDataItem: { Product: { Id, Name } = {} } = {} } = item;
            if (!Id) return acc;
            if (!acc[Id]) {
                acc[Id] = { count: 0, name: Name };
            }
            acc[Id].count += 1;
            return acc;
        }, {});

        const allItems = Object.values(productCount);

        return {
            items: allItems.slice(0, 2),
            moreItemsCount: allItems.length - 2 > 0 ? allItems.length - 2 : 0
        };
    }, [OrderItems]);

    return (
        <Container>
            <Box gap="16px">
                <Box flexDirection={["column", "row"]} gap={["16px", "32px"]}>
                    <Text as="h4" fontSize="22px" fontWeight={700}>
                        {orderDate} | Direct Mail
                    </Text>
                    <Text fontSize="14px">
                        Order reference:{" "}
                        <strong>
                            <span style={{ fontWeight: 700, fontSize: "14px" }}>{OrderNumber}</span>
                        </strong>
                    </Text>
                </Box>
                <Box gap="16px">
                    {showOverview && (
                        <Box>
                            {productList.items.map(({ count, name }) => {
                                if (count > 1) return <Text key={name}>{`${name} x ${count}`}</Text>;
                                return <Text key={name}>{`${name}`}</Text>;
                            })}
                            {!!productList.moreItemsCount && (
                                <Text fontWeight={700} color="#757575" marginTop="8px">
                                    +{productList.moreItemsCount} more items
                                </Text>
                            )}
                        </Box>
                    )}
                    <div>
                        <Tag backgroundColor={tagStyle.backgroundColor} dotColor={tagStyle.dotColor}>
                            {Status?.Name}
                        </Tag>
                    </div>
                </Box>
            </Box>
            <Box flexDirection={["row", "column"]} justifyContent={["space-between", "column"]} alignItems={["flex-end"]}>
                <Box gap={["1.6rem", "4rem"]} flexDirection={["column", "row-reverse"]} alignItems={["flex-start", "center"]}>
                    <Box background="#F3F3F3" padding="12px 16px" borderRadius="5px">
                        <Text as="span" fontSize={["18px", "20px"]} fontWeight={700}>
                            {price}
                        </Text>
                    </Box>
                    {Status.Name === STATUS.APPROVED && !canNavigate && (
                        <Text
                            as="button"
                            type="button"
                            alignItems="center"
                            display="flex"
                            gap="8px"
                            fontWeight={700}
                            color="#008755"
                            fontSize={["14px", "16px"]}
                            onClick={() => {
                                downloadDmOrderInvoice({ orderNumber: OrderNumber });
                            }}
                        >
                            <DownloadIcon /> Download invoice
                        </Text>
                    )}
                </Box>
                {canNavigate && (
                    <Link to={`/Transactions/dm?orderId=${rest.Id}`}>
                        <Text as="span" alignItems="center" display="flex" gap="8px" fontWeight={700} color="#008755" fontSize={["14px", "16px"]}>
                            Details <ArrowRightIcon />
                        </Text>
                    </Link>
                )}
            </Box>
        </Container>
    );
}

function Container({ children }) {
    return (
        <Box
            border="1px solid #CFD0CF"
            borderRadius="5px"
            padding={["16px", "24px"]}
            display="flex"
            flexDirection={["column", "row"]}
            gap="16px"
            justifyContent={["flex-start", "space-between"]}
            width="100%"
        >
            {children}
        </Box>
    );
}

function FilterDayPill({ isActive, ...props }) {
    const hoverStyles = { ":hover": { background: "#E2F3E8" } };
    return <Button borderRadius="50px" fontWeight={400} fontSize="14px" height="40px" background="transparent" {...hoverStyles} {...(isActive ? { background: "#E2F3E8" } : {})} {...props} />;
}

export const FilterButton = styled.button(() => {
    return mq({
        fontSize: "14px",
        height: "36px",
        borderRadius: "50px",
        textAlign: "center",
        fontWeight: 700,
        background: "#008755",
        color: "white",
        padding: "0 18px",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    });
});

export function TransactionsHeader() {
    return (
        <Box gap="16px">
            <Text fontFamily="AnPostSansBold" fontSize={["22px", "26px"]}>
                Transactions
            </Text>
            <Text fontSize={["14px", "16px"]}>Select a product type to view transactions.</Text>
        </Box>
    );
}

export function ProductTabList({ onSelect, selected }) {
    return (
        <Box gap="8px" display="grid" gridTemplateColumns={["1fr 1fr", "repeat(auto-fill, minmax(170px,1fr))"]}>
            <Button className="gtm-tab" variant="secondary-on-grey" isActive={selected === "click-and-post"} onClick={() => onSelect("click-and-post")}>
                Click & Post
            </Button>
            <Button className="gtm-tab" variant="secondary-on-grey" isActive={selected === "direct-mail"} onClick={() => onSelect("direct-mail")}>
                Direct mail
            </Button>
            <Button className="gtm-tab"  variant="secondary-on-grey" isActive={selected === "shop"} onClick={() => onSelect("shop")}>
                Shop
            </Button>
        </Box>
    );
}

export function NoTransactionHistory() {
    return (
        <Box gap={["16px", "24px"]} border="1px solid #C4C4C4" padding={["16px", "24px"]} borderRadius="5px">
            <Box gap="16px">
                <Text fontSize={["20px", "22px"]} fontWeight={700}>
                    You have no transaction history for Direct Mail
                </Text>
                <Text>Are you ready for your next campaign?</Text>
            </Box>
            <Box gap={["16px", "24px"]} display="grid" gridTemplateColumns={["1fr", "220px 220px"]}>
                <Button
                    background="#003D4C"
                    color="white"
                    onClick={() => {
                        window.location.href = `${window.origin}/Commerce/Direct-Mail`;
                    }}
                >
                    Browse all our solutions
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => {
                        window.location.href = `${window.origin}/Commerce/Direct-Mail/Navigator`;
                    }}
                >
                    Use our solution guide
                </Button>
            </Box>
        </Box>
    );
}

export function Filter() {
    const [selected, setSelected] = React.useState([]);
    const [isOpen, setOpen] = React.useState(false);
    const [isApplied, setIsApplied] = React.useState(false);

    const toggle = () => {
        if (isOpen) {
            // reset
            setSelected([]);
            setOpen(false);
            setIsApplied(false);
        } else setOpen(true);
    };

    const onAddOrRemoveFilter = name => {
        const exist = selected.includes(name);
        if (exist) return onRemoveFilter(name);
        return setSelected(prev => [...prev, name]);
    };

    const onRemoveFilter = name => {
        const updated = selected.filter(item => item !== name);
        setSelected(updated);
        if (updated.length === 0) {
            setOpen(false);
            setIsApplied(false);
        }
    };

    const onEdit = () => {
        setOpen(true);
        setIsApplied(false);
    };

    return (
        <Box gap="16px">
            <Box display="block">
                <FilterButton onClick={toggle}>
                    <FilterIcon />
                    Filter results
                </FilterButton>
            </Box>
            {isOpen && !isApplied && <FilterOptions onClick={onAddOrRemoveFilter} toggle={toggle} selected={selected} setIsApplied={() => setIsApplied(true)} onReset={() => setSelected([])} />}
            {isApplied && <FilterApplied selected={selected} onRemove={onRemoveFilter} onEdit={onEdit} />}
        </Box>
    );
}

function FilterOptions({ onClick, setIsApplied, onReset, selected = [], toggle }) {
    return (
        <Box background="#F3F3F3" padding="24px" borderRadius="5px" gap="16px">
            <Box flexDirection={["column-reverse", "row"]} gap={["8px", "40px"]}>
                <Notification />
                <Box display="block" width="auto" marginLeft={["auto", 0]}>
                    <Text as="button" onClick={toggle}>
                        <CloseIcon />
                    </Text>
                </Box>
            </Box>
            <Text fontWeight={700}>Date range</Text>
            <Box display="grid" gap={["16px", "8px"]} gridTemplateColumns={["1fr 1fr", "repeat(auto-fill, minmax(147px,1fr))"]}>
                {["Last 30 days", "Last 3 months", "Last 6 months", "Last 12 months", "Use exact dates"].map(name => {
                    return (
                        <FilterDayPill key={name} isActive={selected.includes(name)} variant="secondary" onClick={() => onClick(name)}>
                            {name}
                        </FilterDayPill>
                    );
                })}
            </Box>
            <Box marginLeft="auto" width={["100%", "auto"]} flexDirection={["column-reverse", "row"]} gap={["16px", "56px"]} marginTop="8px">
                <Text onClick={onReset} as="button" textAlign="center" color="#008755" fontWeight={700}>
                    Reset
                </Text>
                <Button onClick={setIsApplied} isDisabled={selected.length === 0} fontSize={["14px", "16px"]} width={["auto", "170px"]}>
                    Apply filter
                </Button>
            </Box>
        </Box>
    );
}

function FilterApplied({ selected = [], onRemove, onEdit }) {
    return (
        <Box gap="16px">
            <Text fontWeight={700}>Filters applied</Text>
            <Box display="grid" alignItems="center" gap={["16px", "8px"]} gridTemplateColumns={["1fr 1fr", "repeat(auto-fill, minmax(147px,1fr))"]}>
                {selected.map(name => {
                    return (
                        <FilterDayPill key={name} isActive variant="secondary" onClick={() => onRemove(name)}>
                            {name}
                        </FilterDayPill>
                    );
                })}
                <Text onClick={onEdit} marginLeft="auto" as="button" display="flex" gap="8px" fontWeight={700} color="#008755" fontSize={["14px", "16px"]}>
                    <EditIcon /> Edit filter
                </Text>
            </Box>
        </Box>
    );
}

function Notification() {
    return (
        <Box
            padding={["16px", "16px 16px 16px 24px"]}
            flexDirection="row"
            alignItems={["flex-start", "center"]}
            gap={["16px", "24px"]}
            background="#DEEBF5"
            borderRadius="5px"
            border="2px solid #217BBE"
        >
            <div>
                <InfoIcon />
            </div>
            <Text fontWeight={700} fontSize={["13px", "14px"]} lineHeight="22px">
                {/* TODO: Check date */}
                You can search transaction for the last 13 months. The last date you can view transactions for is 01/06/2022
            </Text>
        </Box>
    );
}

function EditIcon() {
    return (
        <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.63639 14.0493H1.03169C0.46271 14.0493 0.00146484 13.5868 0.00146484 13.0162V11.3494C0.00146484 11.0924 0.0969893 10.8446 0.269384 10.6544L7.08661 3.13446L10.1814 6.22925L3.39869 13.7112C3.20344 13.9265 2.92665 14.0493 2.63639 14.0493ZM11.5263 4.74571L8.43151 1.65093L9.57688 0.387483C9.97256 -0.0489811 10.6517 -0.0651876 11.0677 0.35191L12.6997 1.98855C13.0886 2.37849 13.1035 3.00596 12.7335 3.41401L11.5263 4.74571ZM7.00146 12.0493C6.44918 12.0493 6.00146 12.497 6.00146 13.0493C6.00146 13.6016 6.44918 14.0493 7.00146 14.0493H13.0015C13.5537 14.0493 14.0015 13.6016 14.0015 13.0493C14.0015 12.497 13.5537 12.0493 13.0015 12.0493H7.00146Z"
                fill="#008755"
            />
        </svg>
    );
}
function InfoIcon() {
    return (
        <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1.00122" y="1.04932" width="22" height="22" rx="11" stroke="#217BBE" strokeWidth="2" />
            <path d="M12.0012 6.04932L12.0012 7.04932M12.0012 11.0493L12.0012 18.0493" stroke="#217BBE" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M13.0013 3.04933L8.0013 8.04934M8.0013 8.04934L3.00131 13.0493M8.0013 8.04934L13.0013 13.0493M8.0013 8.04934L3.00128 3.04932"
                stroke="#008755"
                strokeWidth="2"
                strokeLinecap="round"
            />
        </svg>
    );
}
function ArrowRightIcon() {
    return (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.79387 2.34221C9.18439 1.95169 9.81756 1.95169 10.2081 2.34221L15.2081 7.34221C15.5986 7.73273 15.5986 8.3659 15.2081 8.75642L10.2081 13.7564C9.81756 14.1469 9.18439 14.1469 8.79387 13.7564C8.40335 13.3659 8.40335 12.7327 8.79387 12.3422L12.0868 9.04932H2.50098C1.94869 9.04932 1.50098 8.6016 1.50098 8.04932C1.50098 7.49703 1.94869 7.04932 2.50098 7.04932H12.0868L8.79387 3.75642C8.40335 3.3659 8.40335 2.73273 8.79387 2.34221Z"
                fill="#008755"
            />
        </svg>
    );
}

function FilterIcon() {
    return (
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_7830_38676)">
                <g clipPath="url(#clip1_7830_38676)">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.73914 11.4795C8.73914 10.6511 9.41071 9.97949 10.2391 9.97949C11.0676 9.97949 11.7391 10.6511 11.7391 11.4795C11.7391 12.3079 11.0676 12.9795 10.2391 12.9795C9.41071 12.9795 8.73914 12.3079 8.73914 11.4795ZM6.88407 12.4795L1.01123 12.4795C0.458946 12.4795 0.0112305 12.0318 0.0112305 11.4795C0.0112305 10.9272 0.458946 10.4795 1.01123 10.4795L6.88408 10.4795C7.31435 9.03374 8.65363 7.97949 10.2391 7.97949C11.8246 7.97949 13.1639 9.03374 13.5942 10.4795H15.0112C15.5635 10.4795 16.0112 10.9272 16.0112 11.4795C16.0112 12.0318 15.5635 12.4795 15.0112 12.4795H13.5942C13.1639 13.9252 11.8246 14.9795 10.2391 14.9795C8.65362 14.9795 7.31434 13.9252 6.88407 12.4795Z"
                        fill="white"
                    />
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.28333 4.6958C7.28333 5.52423 6.61175 6.1958 5.78333 6.1958C4.9549 6.1958 4.28333 5.52423 4.28333 4.6958C4.28333 3.86737 4.9549 3.1958 5.78333 3.1958C6.61175 3.1958 7.28333 3.86737 7.28333 4.6958ZM9.13839 3.69581L15.0112 3.69581C15.5635 3.69581 16.0112 4.14352 16.0112 4.69581C16.0112 5.24809 15.5635 5.69581 15.0112 5.69581L9.13838 5.69581C8.70811 7.14155 7.36883 8.1958 5.78333 8.1958C4.19782 8.1958 2.85854 7.14155 2.42827 5.69581L1.01123 5.69581C0.458945 5.69581 0.0112307 5.24809 0.0112308 4.69581C0.0112308 4.14352 0.458946 3.69581 1.01123 3.69581L2.42826 3.69581C2.85854 2.25006 4.19781 1.1958 5.78333 1.1958C7.36884 1.1958 8.70812 2.25006 9.13839 3.69581Z"
                        fill="white"
                    />
                </g>
            </g>
            <defs>
                <clipPath id="clip0_7830_38676">
                    <rect width="16" height="16" fill="white" transform="translate(0.0012207 0.0493164)" />
                </clipPath>
                <clipPath id="clip1_7830_38676">
                    <rect width="16" height="14" fill="white" transform="translate(0.0012207 1.04932)" />
                </clipPath>
            </defs>
        </svg>
    );
}

function DownloadIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                stroke="#008755"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path d="M4.66675 6.66669L8.00008 10L11.3334 6.66669" stroke="#008755" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 10V2" stroke="#008755" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
